import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Box, Portal } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

/**
 * Własne dymki harmonogramu (prototyp p. Mariusza, zmiana 9: „natychmiastowe,
 * z klawiatury i na dotyku”).
 *
 * Atrybut `title` pokazuje dymek po sekundzie, nie działa z klawiatury ani
 * na dotyku. Tu jeden dymek na cały obszar: element z `data-tip` pokazuje
 * swój tekst od razu po najechaniu albo po dojściu tabulatorem, Escape go
 * chowa, przewinięcie strony też. Na dotyku pierwsze stuknięcie w pasek
 * pokazuje szczegóły, drugie otwiera zlecenie — jak w prototypie.
 *
 * Tekst dymka to zwykły napis z liniami (`\n`); ten sam tekst jest w
 * `data-tip`, więc testy i czytniki ekranu (przez `aria-label` paska) mają
 * go bez otwierania dymka.
 */

interface TipState {
  text: string;
  left: number;
  top: number;
  /** Dymek nad elementem (brak miejsca pod spodem). */
  above: boolean;
}

const TIP_MAX_WIDTH = 380;
const GAP = 8;
/** Zapas na wysokość dymka przy decyzji „pod czy nad” — dymek ma najwyżej kilka linii. */
const ROOM_BELOW = 160;

function tipTarget(target: EventTarget | null, root: HTMLElement | null): HTMLElement | null {
  if (!(target instanceof Element)) return null;
  const el = target.closest<HTMLElement>('[data-tip]');
  return el && root?.contains(el) ? el : null;
}

export const TipLayer = ({ children, sx }: { children: ReactNode; sx?: SxProps<Theme> }) => {
  const root = useRef<HTMLDivElement>(null);
  const pointerType = useRef<string>('mouse');
  const armed = useRef<HTMLElement | null>(null);
  const [tip, setTip] = useState<TipState | null>(null);

  const hide = useCallback(() => setTip(null), []);
  const show = useCallback((el: HTMLElement) => {
    const text = el.dataset.tip;
    if (!text) return;
    const r = el.getBoundingClientRect();
    const viewportW = window.innerWidth || 1024;
    const viewportH = window.innerHeight || 768;
    const above = r.bottom + GAP + ROOM_BELOW > viewportH && r.top > ROOM_BELOW;
    setTip({
      text,
      left: Math.max(GAP, Math.min(r.left, viewportW - TIP_MAX_WIDTH - GAP)),
      top: above ? r.top - GAP : r.bottom + GAP,
      above,
    });
  }, []);

  // Przewinięcie przesuwa elementy pod dymkiem — chowamy go, jak w prototypie.
  useEffect(() => {
    if (!tip) return undefined;
    window.addEventListener('scroll', hide, { capture: true, passive: true });
    return () => window.removeEventListener('scroll', hide, { capture: true });
  }, [tip, hide]);

  return (
    <Box
      ref={root}
      sx={sx}
      onMouseOver={(e) => {
        const el = tipTarget(e.target, root.current);
        if (el) show(el);
        else hide();
      }}
      onMouseLeave={hide}
      onFocus={(e) => {
        const el = tipTarget(e.target, root.current);
        if (el) show(el);
        else hide();
      }}
      onBlur={hide}
      onKeyDown={(e) => {
        if (e.key === 'Escape') hide();
      }}
      onPointerDownCapture={(e) => {
        pointerType.current = e.pointerType;
      }}
      onClickCapture={(e) => {
        if (pointerType.current !== 'touch') return;
        const el = tipTarget(e.target, root.current);
        if (el && el.closest('a') && armed.current !== el) {
          // Pierwsze stuknięcie: szczegóły zamiast przejścia.
          e.preventDefault();
          e.stopPropagation();
          armed.current = el;
          show(el);
        } else if (!el) {
          armed.current = null;
          hide();
        }
      }}
    >
      {children}
      {tip ? (
        <Portal>
          <Box
            role="tooltip"
            data-testid="gantt-tip"
            sx={{
              position: 'fixed',
              left: tip.left,
              top: tip.top,
              transform: tip.above ? 'translateY(-100%)' : undefined,
              zIndex: (theme) => theme.zIndex.tooltip,
              maxWidth: TIP_MAX_WIDTH,
              px: 1.25,
              py: 1,
              borderRadius: 1.5,
              bgcolor: 'grey.900',
              color: 'common.white',
              fontSize: '0.75rem',
              lineHeight: 1.5,
              whiteSpace: 'pre-line',
              boxShadow: 6,
              pointerEvents: 'none',
            }}
          >
            {tip.text}
          </Box>
        </Portal>
      ) : null}
    </Box>
  );
};
